/**
 * AssetDistributionSystem - Verteilt externe Asset-Sammlung auf OpenStreetMap
 * 
 * Lädt eine komplette Asset-Bibliothek (Gebäude, Straßen-Props, Vegetation)
 * und verteilt diese intelligent basierend auf OSM-Daten:
 * - Gebäude-GLBs auf OSM-Gebäude-Footprints
 * - Straßen-Props entlang von Straßen
 * - Vegetation prozedural in freien Flächen
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { RoadSegment, BuildingData } from './OpenWorldMapLoader';

// Asset-Katalog Typen
export interface AssetLibrary {
  buildings: BuildingAsset[];
  streetProps: PropAsset[];
  vegetation: VegetationAsset[];
  roads: RoadAsset[];
}

export interface BuildingAsset {
  id: string;
  name: string;
  path: string;
  category: 'residential' | 'commercial' | 'industrial' | 'office' | 'landmark';
  footprintSize: { width: number; depth: number }; // in Metern
  height: number;
  weight: number; // Wahrscheinlichkeit 0-1
}

export interface PropAsset {
  id: string;
  name: string;
  path: string;
  category: 'bench' | 'bin' | 'bollard' | 'sign' | 'booth' | 'hydrant' | 'mailbox' | 'bike_rack';
  scale: number;
  roadTypes: string[]; // Auf welchen Straßentypen
  spacing: number; // Durchschnittlicher Abstand in Metern
  weight: number;
}

export interface VegetationAsset {
  id: string;
  name: string;
  path: string;
  category: 'tree' | 'bush' | 'grass' | 'flower';
  scale: { min: number; max: number };
  density: number; // Pro Quadratmeter
  weight: number;
}

export interface RoadAsset {
  id: string;
  name: string;
  path: string;
  category: 'texture' | 'marking' | 'curb' | 'barrier';
  roadTypes: string[];
}

export interface AssetDistributionOptions {
  maxBuildings?: number;
  maxProps?: number;
  maxVegetation?: number;
  buildingDensity?: number; // 0-1: Wie viele OSM-Gebäude bekommen Assets
  propDensity?: number;
  vegetationDensity?: number;
  enableLOD?: boolean;
  loadRadius?: number; // Radius in Metern um Spieler
}

export class AssetDistributionSystem {
  private scene: THREE.Scene;
  private loader = new GLTFLoader();
  
  // Asset-Caches
  private buildingCache = new Map<string, THREE.Group>();
  private propCache = new Map<string, THREE.Group>();
  private vegetationCache = new Map<string, THREE.Group>();
  
  // Platzierte Assets
  private placedBuildings: THREE.Object3D[] = [];
  private placedProps: THREE.Object3D[] = [];
  private placedVegetation: THREE.Object3D[] = [];
  
  // Asset-Bibliothek
  private library: AssetLibrary | null = null;
  
  // Instanced Meshes für Performance
  private vegetationInstances = new Map<string, THREE.InstancedMesh>();
  private propInstances = new Map<string, THREE.InstancedMesh>();
  
  private group = new THREE.Group();
  private options: Required<AssetDistributionOptions>;
  
  constructor(
    scene: THREE.Scene,
    options: AssetDistributionOptions = {}
  ) {
    this.scene = scene;
    this.group.name = 'AssetDistribution';
    this.scene.add(this.group);
    
    this.options = {
      maxBuildings: options.maxBuildings ?? 500,
      maxProps: options.maxProps ?? 2000,
      maxVegetation: options.maxVegetation ?? 5000,
      buildingDensity: options.buildingDensity ?? 0.7,
      propDensity: options.propDensity ?? 0.5,
      vegetationDensity: options.vegetationDensity ?? 0.3,
      enableLOD: options.enableLOD ?? true,
      loadRadius: options.loadRadius ?? 500
    };
  }
  
  /**
   * Lädt eine komplette Asset-Bibliothek
   */
  async loadAssetLibrary(library: AssetLibrary): Promise<void> {
    this.library = library;
    console.log('📦 Lade Asset-Bibliothek...');
    console.log(`  - ${library.buildings.length} Gebäude-Assets`);
    console.log(`  - ${library.streetProps.length} Straßen-Props`);
    console.log(`  - ${library.vegetation.length} Vegetations-Assets`);
    console.log(`  - ${library.roads.length} Straßen-Assets`);
    
    // Pre-load häufig verwendete Assets
    const toPreload = [
      ...library.buildings.filter(b => b.weight > 0.7).slice(0, 5),
      ...library.streetProps.filter(p => p.weight > 0.6).slice(0, 10),
      ...library.vegetation.filter(v => v.weight > 0.7).slice(0, 5)
    ];
    
    for (const asset of toPreload) {
      try {
        if ('footprintSize' in asset) {
          await this.loadBuildingAsset(asset as BuildingAsset);
        } else if ('spacing' in asset) {
          await this.loadPropAsset(asset as PropAsset);
        } else {
          await this.loadVegetationAsset(asset as VegetationAsset);
        }
      } catch (err) {
        console.warn(`Fehler beim Vorladen von ${asset.name}:`, err);
      }
    }
    
    console.log('✅ Asset-Bibliothek geladen');
  }
  
  /**
   * Verteilt Gebäude-Assets auf OSM-Gebäuden
   */
  async distributeBuildings(buildings: BuildingData[]): Promise<void> {
    if (!this.library) {
      console.warn('Asset-Bibliothek nicht geladen');
      return;
    }
    
    const buildingsToPlace = buildings
      .filter(() => Math.random() < this.options.buildingDensity)
      .slice(0, this.options.maxBuildings);
    
    console.log(`🏢 Verteile ${buildingsToPlace.length} Gebäude...`);
    
    for (const building of buildingsToPlace) {
      try {
        // Finde passendes Asset basierend auf Footprint-Größe
        const asset = this.selectBuildingAsset(building);
        if (!asset) continue;
        
        const model = await this.loadBuildingAsset(asset);
        const instance = model.clone();
        
        // Positioniere auf Footprint-Zentrum
        const center = this.calculateFootprintCenter(building.footprint);
        instance.position.copy(center);
        
        // Skaliere basierend auf Gebäudegröße
        const scale = this.calculateBuildingScale(building, asset);
        instance.scale.setScalar(scale);
        
        // Rotiere zufällig (90° Schritte)
        instance.rotation.y = (Math.floor(Math.random() * 4) * Math.PI) / 2;
        
        this.group.add(instance);
        this.placedBuildings.push(instance);
      } catch (err) {
        console.warn(`Fehler beim Platzieren von Gebäude:`, err);
      }
    }
    
    console.log(`✅ ${this.placedBuildings.length} Gebäude platziert`);
  }
  
  /**
   * Verteilt Straßen-Props entlang von Straßen
   */
  async distributeStreetProps(roads: RoadSegment[]): Promise<void> {
    if (!this.library) return;
    
    console.log(`🚦 Verteile Straßen-Props...`);
    let propCount = 0;
    
    for (const road of roads) {
      if (propCount >= this.options.maxProps) break;
      if (Math.random() > this.options.propDensity) continue;
      
      // Wähle passende Props für diesen Straßentyp
      const suitableProps = this.library.streetProps.filter(p =>
        p.roadTypes.includes(road.type)
      );
      
      if (suitableProps.length === 0) continue;
      
      // Platziere Props entlang der Straße
      for (let i = 0; i < road.points.length - 1; i++) {
        const p1 = road.points[i];
        const p2 = road.points[i + 1];
        const dist = p1.distanceTo(p2);
        
        // Wähle zufälliges Prop
        const prop = this.weightedRandomSelect(suitableProps);
        const spacing = prop.spacing * (0.8 + Math.random() * 0.4);
        const numProps = Math.floor(dist / spacing);
        
        for (let j = 0; j < numProps && propCount < this.options.maxProps; j++) {
          try {
            const t = j / Math.max(1, numProps - 1);
            const pos = new THREE.Vector3().lerpVectors(p1, p2, t);
            
            // Verschiebe seitlich (Bürgersteig)
            const offset = road.width * 0.5 + 1.5; // 1.5m Bürgersteig
            const dir = new THREE.Vector3().subVectors(p2, p1).normalize();
            const perp = new THREE.Vector3(-dir.z, 0, dir.x);
            const side = Math.random() < 0.5 ? 1 : -1;
            pos.add(perp.multiplyScalar(offset * side));
            
            const model = await this.loadPropAsset(prop);
            const instance = model.clone();
            instance.position.copy(pos);
            instance.rotation.y = Math.random() * Math.PI * 2;
            instance.scale.setScalar(prop.scale * (0.9 + Math.random() * 0.2));
            
            this.group.add(instance);
            this.placedProps.push(instance);
            propCount++;
          } catch (err) {
            console.warn(`Fehler beim Platzieren von Prop:`, err);
          }
        }
      }
    }
    
    console.log(`✅ ${propCount} Props platziert`);
  }
  
  /**
   * Verteilt Vegetation prozedural
   */
  async distributeVegetation(center: THREE.Vector3, radius: number): Promise<void> {
    if (!this.library) return;
    
    console.log(`🌳 Verteile Vegetation (${radius}m Radius)...`);
    
    const area = Math.PI * radius * radius;
    const numVeg = Math.min(
      this.options.maxVegetation,
      Math.floor(area * this.options.vegetationDensity * 0.01)
    );
    
    for (let i = 0; i < numVeg; i++) {
      try {
        // Zufällige Position im Radius
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.sqrt(Math.random()) * radius;
        const pos = center.clone().add(
          new THREE.Vector3(
            Math.cos(angle) * dist,
            0,
            Math.sin(angle) * dist
          )
        );
        
        // Wähle zufälliges Vegetations-Asset
        const asset = this.weightedRandomSelect(this.library.vegetation);
        const model = await this.loadVegetationAsset(asset);
        const instance = model.clone();
        
        instance.position.copy(pos);
        instance.rotation.y = Math.random() * Math.PI * 2;
        const scale = THREE.MathUtils.lerp(asset.scale.min, asset.scale.max, Math.random());
        instance.scale.setScalar(scale);
        
        this.group.add(instance);
        this.placedVegetation.push(instance);
      } catch (err) {
        console.warn(`Fehler beim Platzieren von Vegetation:`, err);
      }
    }
    
    console.log(`✅ ${this.placedVegetation.length} Vegetations-Objekte platziert`);
  }
  
  /**
   * Lädt ein Gebäude-Asset
   */
  private async loadBuildingAsset(asset: BuildingAsset): Promise<THREE.Group> {
    if (this.buildingCache.has(asset.id)) {
      return this.buildingCache.get(asset.id)!.clone();
    }
    
    const gltf = await this.loader.loadAsync(asset.path);
    const model = gltf.scene;
    
    model.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) {
        const mesh = node as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    
    this.buildingCache.set(asset.id, model);
    return model.clone();
  }
  
  /**
   * Lädt ein Prop-Asset
   */
  private async loadPropAsset(asset: PropAsset): Promise<THREE.Group> {
    if (this.propCache.has(asset.id)) {
      return this.propCache.get(asset.id)!.clone();
    }
    
    const gltf = await this.loader.loadAsync(asset.path);
    const model = gltf.scene;
    
    model.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) {
        (node as THREE.Mesh).castShadow = true;
      }
    });
    
    this.propCache.set(asset.id, model);
    return model.clone();
  }
  
  /**
   * Lädt ein Vegetations-Asset
   */
  private async loadVegetationAsset(asset: VegetationAsset): Promise<THREE.Group> {
    if (this.vegetationCache.has(asset.id)) {
      return this.vegetationCache.get(asset.id)!.clone();
    }
    
    const gltf = await this.loader.loadAsync(asset.path);
    const model = gltf.scene;
    
    model.traverse((node) => {
      if ((node as THREE.Mesh).isMesh) {
        (node as THREE.Mesh).castShadow = true;
      }
    });
    
    this.vegetationCache.set(asset.id, model);
    return model.clone();
  }
  
  /**
   * Wählt passendes Gebäude-Asset basierend auf OSM-Gebäude
   */
  private selectBuildingAsset(building: BuildingData): BuildingAsset | null {
    if (!this.library) return null;
    
    // Berechne Gebäudegröße
    const size = this.calculateFootprintSize(building.footprint);
    
    // Filtere passende Assets
    const suitable = this.library.buildings.filter(asset => {
      const sizeRatio = Math.max(size.width, size.depth) / Math.max(asset.footprintSize.width, asset.footprintSize.depth);
      return sizeRatio > 0.5 && sizeRatio < 2.0; // Ähnliche Größe
    });
    
    if (suitable.length === 0) return null;
    return this.weightedRandomSelect(suitable);
  }
  
  /**
   * Gewichtete Zufallsauswahl
   */
  private weightedRandomSelect<T extends { weight: number }>(items: T[]): T {
    const totalWeight = items.reduce((sum, item) => sum + item.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const item of items) {
      random -= item.weight;
      if (random <= 0) return item;
    }
    
    return items[items.length - 1];
  }
  
  /**
   * Berechnet Zentrum eines Footprints
   */
  private calculateFootprintCenter(footprint: THREE.Vector3[]): THREE.Vector3 {
    const center = new THREE.Vector3();
    for (const point of footprint) {
      center.add(point);
    }
    center.divideScalar(footprint.length);
    return center;
  }
  
  /**
   * Berechnet Footprint-Größe
   */
  private calculateFootprintSize(footprint: THREE.Vector3[]): { width: number; depth: number } {
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    for (const point of footprint) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minZ = Math.min(minZ, point.z);
      maxZ = Math.max(maxZ, point.z);
    }
    
    return {
      width: maxX - minX,
      depth: maxZ - minZ
    };
  }
  
  /**
   * Berechnet Skalierungsfaktor für Gebäude
   */
  private calculateBuildingScale(building: BuildingData, asset: BuildingAsset): number {
    const bSize = this.calculateFootprintSize(building.footprint);
    const aSize = asset.footprintSize;
    
    const scaleX = bSize.width / aSize.width;
    const scaleZ = bSize.depth / aSize.depth;
    
    return (scaleX + scaleZ) / 2;
  }
  
  /**
   * Räumt Assets auf (z.B. außerhalb des Laderadius)
   */
  cleanupDistantAssets(center: THREE.Vector3, radius: number): void {
    const cleanup = (assets: THREE.Object3D[]) => {
      for (let i = assets.length - 1; i >= 0; i--) {
        const asset = assets[i];
        const dist = asset.position.distanceTo(center);
        
        if (dist > radius) {
          this.group.remove(asset);
          assets.splice(i, 1);
        }
      }
    };
    
    cleanup(this.placedBuildings);
    cleanup(this.placedProps);
    cleanup(this.placedVegetation);
  }
  
  /**
   * Dispose
   */
  dispose(): void {
    this.scene.remove(this.group);
    this.buildingCache.clear();
    this.propCache.clear();
    this.vegetationCache.clear();
    this.placedBuildings = [];
    this.placedProps = [];
    this.placedVegetation = [];
  }
  
  /**
   * Statistiken
   */
  getStats() {
    return {
      buildings: this.placedBuildings.length,
      props: this.placedProps.length,
      vegetation: this.placedVegetation.length,
      cached: {
        buildings: this.buildingCache.size,
        props: this.propCache.size,
        vegetation: this.vegetationCache.size
      }
    };
  }
}

export default AssetDistributionSystem;
